import { useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronUp, Loader2, X } from 'lucide-react';
import { UPLOAD_IN_FLIGHT, type SpaceUploads, type UploadItem, type UploadState } from '../../lib/space-upload';
import { formatBytes } from '../../lib/spaces';
import { cn } from '../../lib/utils';

const STATE_LABEL: Record<UploadState, string> = {
  queued: 'Waiting',
  hashing: 'Checking',
  requesting: 'Starting',
  uploading: 'Uploading',
  confirming: 'Finishing',
  done: 'Uploaded',
  failed: 'Failed',
  conflict: 'Name taken',
  cancelled: 'Cancelled',
};

function Row({ item, uploads }: { item: UploadItem; uploads: SpaceUploads }) {
  const moving = UPLOAD_IN_FLIGHT.has(item.state);
  const share = item.file.size > 0 ? Math.min(1, item.loaded / item.file.size) : item.state === 'done' ? 1 : 0;
  const cancellable = item.state === 'queued' || item.state === 'hashing' || item.state === 'requesting' || item.state === 'uploading';

  return (
    <li className="px-3 py-2 border-b border-border-subtle last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        {item.state === 'done' ? (
          <Check size={12} className="text-success-text flex-shrink-0" />
        ) : item.state === 'failed' || item.state === 'conflict' ? (
          <AlertTriangle size={12} className={cn('flex-shrink-0', item.state === 'failed' ? 'text-danger-text' : 'text-warning-text')} />
        ) : moving ? (
          <Loader2 size={12} className="text-orange-500 animate-spin flex-shrink-0" />
        ) : (
          <X size={12} className="text-ink-faint flex-shrink-0" />
        )}
        <span className="text-xs text-ink truncate flex-1" title={`${item.destination}${item.path}`}>
          {item.path}
          {item.savedAs && <span className="text-ink-faint"> → saved as {item.savedAs}</span>}
        </span>
        <span className="text-[10px] text-ink-faint tabular-nums flex-shrink-0">
          {item.state === 'uploading' ? `${Math.round(share * 100)}%` : STATE_LABEL[item.state]}
        </span>
        {cancellable ? (
          <button type="button" onClick={() => uploads.cancel(item.key)} title="Cancel" className="p-0.5 rounded text-ink-faint hover:text-ink">
            <X size={11} />
          </button>
        ) : !moving ? (
          <button type="button" onClick={() => uploads.dismiss(item.key)} title="Remove from list" className="p-0.5 rounded text-ink-faint hover:text-ink">
            <X size={11} />
          </button>
        ) : null}
      </div>
      <p className="mt-0.5 pl-5 text-[10px] text-ink-faint truncate">
        {formatBytes(item.file.size)} · to <span className="font-mono">{item.destination}</span>
      </p>
      {(item.state === 'uploading' || item.state === 'confirming') && (
        <div className="mt-1 ml-5 h-1 rounded-sm bg-surface-muted overflow-hidden">
          <div className="h-full bg-accent transition-[width]" style={{ width: `${share * 100}%` }} />
        </div>
      )}
      {item.error && (
        <p className={cn('mt-1 pl-5 text-[11px] leading-snug', item.state === 'conflict' ? 'text-warning-text' : 'text-danger-text')}>{item.error}</p>
      )}
      {item.state === 'conflict' && (
        <div className="mt-1.5 pl-5 flex items-center gap-3">
          <button type="button" onClick={() => uploads.resolveConflict(item.key, 'replace')} className="text-[11px] font-medium text-orange-600 dark:text-orange-400 hover:underline">
            Replace
          </button>
          <button type="button" onClick={() => uploads.resolveConflict(item.key, 'rename')} className="text-[11px] font-medium text-orange-600 dark:text-orange-400 hover:underline">
            Keep both
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * Every upload started from this page, wherever it was going.
 *
 * Mounted by the page rather than the listing, because an upload outlives the folder it was dropped
 * on: moving to another folder mid-batch must not lose sight of it. Replace makes the dropped file a
 * new version of the one in the way (the old one stays in its history); Keep both stores it beside it
 * under a name the server picks.
 */
export function UploadTray({ uploads }: { uploads: SpaceUploads }) {
  const [collapsed, setCollapsed] = useState(false);
  const { items } = uploads;
  if (items.length === 0) return null;

  const moving = items.filter((i) => UPLOAD_IN_FLIGHT.has(i.state)).length;
  const done = items.filter((i) => i.state === 'done').length;
  const failed = items.filter((i) => i.state === 'failed').length;
  const conflicts = items.filter((i) => i.state === 'conflict').length;
  const summary = moving > 0
    ? `Uploading ${moving} file${moving === 1 ? '' : 's'}`
    : `${done} uploaded${failed ? ` · ${failed} failed` : ''}${conflicts ? ` · ${conflicts} need a decision` : ''}`;

  return (
    <div
      className="fixed bottom-4 z-40 w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-card shadow-xl overflow-hidden"
      style={{ right: 'calc(var(--rail-w) + 1rem)' }}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-surface-subtle">
        {moving > 0 && <Loader2 size={12} className="text-orange-500 animate-spin" />}
        <span className="text-xs font-medium text-ink flex-1 truncate">{summary}</span>
        {moving === 0 && (
          <button type="button" onClick={uploads.clearFinished} className="text-[11px] text-ink-muted hover:text-ink">
            Clear
          </button>
        )}
        <button type="button" onClick={() => setCollapsed((c) => !c)} className="p-0.5 rounded text-ink-faint hover:text-ink" aria-label={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>
      {!collapsed && (
        <>
          {conflicts > 1 && (
            <div className="flex items-center gap-3 px-3 py-2 border-b border-border-subtle bg-warning-bg">
              <span className="text-[11px] text-warning-text flex-1">{conflicts} names are taken.</span>
              <button type="button" onClick={() => uploads.resolveAllConflicts('replace')} className="text-[11px] font-medium text-warning-text hover:underline">
                Replace all
              </button>
              <button type="button" onClick={() => uploads.resolveAllConflicts('rename')} className="text-[11px] font-medium text-warning-text hover:underline">
                Keep both for all
              </button>
            </div>
          )}
          <ul className="max-h-72 overflow-y-auto">
            {items.map((item) => (
              <Row key={item.key} item={item} uploads={uploads} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
